import { useCallback, useMemo, useState } from 'react';
import { Icon, IconButton, useToast } from '@chakra-ui/react';
import { RiDeleteBinLine, RiRefreshLine } from 'react-icons/ri';
import axios from 'axios';
import { useAuth } from '../../../../../hooks/auth';
import { ConfirmationModal } from '../../../../../components/ConfirmationModal';
import { translateError } from '../../../../../utils/errors';
import deactivateVentureUserAssignmentService from '../../../../../services/Users/DeactivateVentureUserAssignmentService';
import reactivateVentureUserAssignmentService from '../../../../../services/Users/ReactivateVentureUserAssignmentService';

interface ITargetAssignment {
  ventureId?: string;
  deletedAt?: string;
  featureGroup?: { authLevel?: number; key?: string; name?: string };
}

interface ITargetUser {
  id: string;
  name?: string;
  userAssignments: ITargetAssignment[];
}

interface IVentureAssignmentActionsProps {
  user: ITargetUser;
  onChanged: () => void;
  size?: string;
}

export const VentureAssignmentActions = ({
  user,
  onChanged,
  size = 'sm',
}: IVentureAssignmentActionsProps): JSX.Element | null => {
  const toast = useToast();

  const { assignment: authenticatedAssignment, venture: authenticatedVenture } =
    useAuth();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const targetAssignment = useMemo(() => {
    if (!user.userAssignments?.length) return undefined;

    return (
      user.userAssignments.find(
        (assignment) => assignment.ventureId === authenticatedVenture?.id,
      ) || user.userAssignments[0]
    );
  }, [user.userAssignments, authenticatedVenture?.id]);

  const isDeleted = !!targetAssignment?.deletedAt;

  const canManage = useMemo(() => {
    if (!authenticatedAssignment || !targetAssignment) return false;

    // Exclusão/reativação por empreendimento é exclusiva do perfil Gerente.
    if (!authenticatedAssignment.featureGroup.key.includes('MANAGER')) {
      return false;
    }

    // Gerente só gerencia perfis de nível abaixo do seu.
    return (
      authenticatedAssignment.featureGroup.authLevel <
      (targetAssignment.featureGroup?.authLevel ?? 0)
    );
  }, [authenticatedAssignment, targetAssignment]);

  const handleToggleModal = useCallback(() => {
    setIsModalOpen((prevState) => !prevState);
  }, []);

  const handleConfirm = useCallback(
    async (reason?: string) => {
      setIsSubmitting(true);

      try {
        if (isDeleted) {
          await reactivateVentureUserAssignmentService({
            userId: user.id,
            reason,
          });
        } else {
          await deactivateVentureUserAssignmentService({
            userId: user.id,
            reason,
          });
        }

        toast({
          title: isDeleted ? 'Reativado com sucesso' : 'Excluído com sucesso',
          description: isDeleted
            ? 'O usuário foi reativado no empreendimento.'
            : 'O usuário foi excluído do empreendimento.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });

        setIsModalOpen(false);
        onChanged();
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: isDeleted ? 'Falha ao reativar' : 'Falha ao excluir',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro, tente novamente.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [isDeleted, user.id, toast, onChanged],
  );

  if (!canManage) return null;

  const actionLabel = isDeleted
    ? 'Reativar no empreendimento'
    : 'Excluir do empreendimento';

  return (
    <>
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={handleToggleModal}
        onConfirm={handleConfirm}
        isConfirming={isSubmitting}
        title={
          isDeleted ? 'Reativar no empreendimento' : 'Excluir do empreendimento'
        }
        message={
          isDeleted
            ? `Deseja reativar ${
                user.name || 'este usuário'
              } no empreendimento ${authenticatedVenture?.name || 'atual'}?`
            : `Deseja excluir ${
                user.name || 'este usuário'
              } do empreendimento ${
                authenticatedVenture?.name || 'atual'
              }? Se for um titular, toda a árvore de membresia (dependentes e convidados) será excluída neste empreendimento.`
        }
        showReasonInput
      />

      <IconButton
        size={size}
        variant={isDeleted ? 'solid' : 'ghost'}
        colorScheme={isDeleted ? 'green' : 'red'}
        isLoading={isSubmitting}
        onClick={handleToggleModal}
        aria-label={actionLabel}
        icon={
          <Icon
            as={isDeleted ? RiRefreshLine : RiDeleteBinLine}
            fontSize="18"
          />
        }
      />
    </>
  );
};

export default VentureAssignmentActions;
